import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Button,
  Modal,
  Typography,
  Stack,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import Calculation from "./Calculation";
import { useSnackBar } from "context/snackbarContext";
import { clearTableData } from "views/Sales-Sections/salesReturn/slice/tableDataSlice";
import { setToDefaultCalc } from "../slice/discAndTaxSlice";
import { updateSelected } from "../slice/returnHeaderSlice";
import { baseUrl, redirectUrl } from "config";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { stringToHex, hexToBase64 } from "utils/conversionUtils";
import FileUpload from "ui-component/FileUpload/FileUpload";
import getConditionStatus from "utils/getPrevStatusFunc";
import filterStatusValue from "utils/filterStatusValue";
import { CommonPrivFilter } from "utils/Privileges";

//--------// APIS_USED_FOR_THIS_COMPONENT //---------//
const GET_RETURN_NO = "/getMaxBillSr";
const DELETE_BILL = "/deleteReturnInvoice";
const CUSTOMER_OUTLET_DEALER = "/getMasterAndSalesItem";
const SAVE_RETURN_INVOICE = "/saveReturnInvoice";
const UPDATE_RETURN_INVOICE = "/updateReturnInvoice";
const GET_TERMINAL_PRINT_ITEMS = "/getTermelPrintItems";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  border: "0px",
  boxShadow: 24,
  p: 2,
  borderRadius: 2,
};

function Footer() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(false);
  const [modalTyp, setModalTyp] = useState("");
  const [qrData, setQrData] = useState("");
  const [billData, setBillData] = useState({});
  const [allData, setAllData] = useState([]);
  const theme = useTheme();
  const { showSnackbar } = useSnackBar();
  const dispatch = useDispatch();

  // Consolidate useSelector calls for efficiency
  const {
    returnHeader,
    tableData,
    discPer,
    discAmtByPer,
    discAmt,
    taxPer,
    taxAmt,
    other,
    total,
    netTotal,
    companyInfo,
    printrtTyp,
    decimalCount,
    commonState,
  } = useSelector((state) => ({
    returnHeader: state.returnHeader,
    tableData: state.tableData, //salesReturnbill
    discPer: state.discAndTax.discPer, //discPerc
    discAmtByPer: state.discAndTax.discAmtByPer,
    discAmt: state.discAndTax.discAmt, //discAmt
    taxPer: state.discAndTax.taxPer, //tax
    taxAmt: state.discAndTax.taxAmt, //taxAmt
    other: state.discAndTax.other,
    total: state.discAndTax.total, //total
    netTotal: state.discAndTax.netTotal, //netTotal
    companyInfo: state.common.companyInfo,
    printrtTyp: state.common.printrtTyp,
    decimalCount: state.common.decimalCount,
    commonState: state.common,
  }));
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const mode = "SALES RETURN";

  // Memoized handlers for modal and file upload
  const handleFileClose = useCallback(() => {
    setOpenFileUpload(false);
  }, []);

  const handleAttach = useCallback(() => {
    setOpenFileUpload(true);
  }, []);

  const handleClose = useCallback(() => {
    setProgress(false);
    setOpen(false);
  }, []);

  // Memoized function to fetch and update header data (customer, outlet, dealer)
  const fetchHeaderMasterData = useCallback(async () => {
    try {
      const res = await axios.get(baseUrl + CUSTOMER_OUTLET_DEALER, {
        withCredentials: true,
      });
      dispatch(
        updateSelected({
          customer: res?.data?.[0]?.[0],
          contact: res?.data?.[0]?.[0]?.Ph,
          outlet: res?.data?.[2]?.[0],
          dealer: res?.data?.[1]?.[0],
        }),
      );
    } catch (error) {
      console.error("Error fetching master data:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        redirectUrl();
      } else {
        showSnackbar("error", "Failed to load master data.");
      }
    }
  }, [dispatch, showSnackbar]);

  // Memoized function to get and update the maximum return number
  const getLatestReturnNo = useCallback(async () => {
    try {
      const res = await axios.get(baseUrl + GET_RETURN_NO, {
        withCredentials: true,
      });
      dispatch(
        updateSelected({
          retNo: res.data[0].BillNo,
          retBill: res.data[0].BillNo,
        }),
      );
    } catch (error) {
      console.error("Error fetching return number:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        redirectUrl();
      } else {
        showSnackbar("error", "Failed to get return number.");
      }
    }
  }, [dispatch, showSnackbar]);

  // Combined logic for setting new return number and clearing data
  const handleRetNO = useCallback(() => {
    fetchHeaderMasterData();
    getLatestReturnNo();
  }, [fetchHeaderMasterData, getLatestReturnNo]);

  // Helper function for QR code generation, called internally by handlePrint
  const handlePrintQrcodeGenerate = useCallback(
    (data) => {
      if (!companyInfo || !data) return;

      const companyNameOg = companyInfo?.Name || "";
      const companyNameOgHex = stringToHex(companyNameOg);

      const vatNumberOg = companyInfo?.Add2 || "";
      const vatNumberOgHex = stringToHex(vatNumberOg);

      const inputDate = dayjs(data.BillDate);
      const invoiceDateOg = inputDate.toISOString();
      const invoiceDateHex = stringToHex(invoiceDateOg);

      const totalOg = data?.BillNetTotalField?.toString(16) || "";
      const vatTotalOg = data?.TItTaxAmt?.toString(16) || "";

      const companyNameOgLeng = companyNameOg.length
        .toString(16)
        .padStart(2, "0");
      const vatNumberOgLeng = vatNumberOg.length.toString(16).padStart(2, "0");
      const invoiceDateOGLeng = invoiceDateOg.length
        .toString(16)
        .padStart(2, "0");
      const totalOgLeng = totalOg.length.toString(16).padStart(2, "0");
      const vatTotalOgLeng = vatTotalOg.length.toString(16).padStart(2, "0");

      const value = `01${companyNameOgLeng}${companyNameOgHex}02${vatNumberOgLeng}${vatNumberOgHex}03${invoiceDateOGLeng}${invoiceDateHex}04${totalOgLeng}${totalOg}05${vatTotalOgLeng}${vatTotalOg}`;
      const base64String = hexToBase64(value);
      setQrData(base64String);
    },
    [companyInfo],
  );

  // Initial load effect: get latest return number and clear form
  // ONLY CALL handleRetNO here, as handleClearData itself calls handleRetNO
  // Calling handleClearData here would cause a loop
  useEffect(() => {
    handleRetNO();
  }, [handleRetNO]); // Depend on handleRetNO to ensure it runs after it's defined and stable

  // Effect for navigation to printer page after QR data and allData are ready
  useEffect(() => {
    if (allData.length > 0 && qrData) {
      const printTypeConfig = filterStatusValue(
        printrtTyp[0],
        "Typ",
        "WEB_PRINT",
      )[0];
      const printTypeDescription = printTypeConfig
        ? printTypeConfig.Description
        : "DefaultPrint";

      navigate("/printer/printerCheck", {
        state: {
          data: allData[0][0],
          tableData: allData[1],
          qrData: qrData,
          printType: printTypeDescription,
          typ: "SR",
        },
      });
      // Clear data and reset state after successful print navigation
      dispatch(clearTableData());
      dispatch(setToDefaultCalc());
      dispatch(
        updateSelected({
          edit: false,
          date: dayjs(),
        }),
      );
      handleRetNO(); // Get new return number after printing
    }
  }, [qrData, allData, dispatch, navigate, handleRetNO, printrtTyp]);

  // Memoized function to handle printing API call
  const handlePrint = useCallback(async () => {
    if (!returnHeader.retNo) {
      showSnackbar("warning", "No return number to print.");
      return;
    }
    try {
      const response = await axios.post(
        baseUrl + GET_TERMINAL_PRINT_ITEMS,
        {
          BillNo: returnHeader.retNo,
          typ: "SR",
        },
        { withCredentials: true },
      );
      if (response.data && response.data[0] && response.data[1]) {
        setBillData(response.data[0][0]);
        setAllData(response.data);
        handlePrintQrcodeGenerate(response.data[0][0]); // Generate QR code immediately after getting bill data
      } else {
        showSnackbar("info", "No data found to print for this return.");
      }
    } catch (error) {
      console.error("Error fetching print items:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        redirectUrl();
      } else {
        showSnackbar("error", "Failed to fetch print details.");
      }
    }
  }, [returnHeader.retNo, showSnackbar, handlePrintQrcodeGenerate]);

  // Memoized function to handle form submission (save/update)
  const handleSubmit = useCallback(
    async (dat) => {
      if (tableData.length === 0) {
        showSnackbar("warning", "Add Products to the return first.");
        return;
      }

      setProgress(true);
      const totalTableTax = tableData.reduce((acc, cur) => acc + cur.taxAmt, 0);
      const saveOrUpdateEndpoint = returnHeader.edit
        ? UPDATE_RETURN_INVOICE
        : SAVE_RETURN_INVOICE;

      const salesReturnPayload = {
        salesReturnbill: tableData,
        header: returnHeader,
        discPerc: discPer,
        discAmtByPer,
        discAmt: discAmt,
        tax: taxPer,
        taxAmt: parseFloat(taxAmt.truncateDecimal(decimalCount)), // Ensure correct precision
        total: total,
        netTotal: netTotal,
        otherChrgs: other,
        netQty: tableData.length,
        totalTableTax: parseFloat(totalTableTax.truncateDecimal(decimalCount)), // Ensure correct precision
      };

      try {
        const res = await axios.post(
          baseUrl + saveOrUpdateEndpoint,
          salesReturnPayload,
          { withCredentials: true },
        );
        if (res.data.success) {
          showSnackbar("success", res.data.message);
          dispatch(clearTableData());
          handleRetNO(); // Get new return number
          setOpen(false); // Close modal
          dispatch(setToDefaultCalc());
          dispatch(
            updateSelected({
              edit: false,
              date: dayjs(),
            }),
          );
          if (dat === "saveAndPrint") {
            handlePrint();
          }
        } else {
          showSnackbar("error", res.data.message);
        }
      } catch (error) {
        console.error("Error saving sales return:", error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          redirectUrl();
        } else {
          showSnackbar(
            "error",
            "Failed to save sales return. Please try again.",
          );
        }
      } finally {
        setProgress(false);
      }
    },
    [
      tableData,
      returnHeader,
      discPer,
      discAmtByPer,
      discAmt,
      taxPer,
      taxAmt,
      total,
      netTotal,
      other,
      decimalCount,
      showSnackbar,
      dispatch,
      handleRetNO,
      handlePrint,
    ],
  );

  // Memoized function to open modals (save/delete confirmation)
  const openModal = useCallback(
    (typ) => {
      setModalTyp(typ);
      if (typ === "save") {
        if (tableData.length > 0) {
          setOpen(true);
        } else {
          showSnackbar("warning", "Add Products to save the return.");
        }
      } else {
        // For delete modal
        setOpen(true);
      }
    },
    [tableData.length, showSnackbar],
  );

  // Memoized function to handle deleting a return
  const handleDeleteRet = useCallback(async () => {
    setProgress(true);
    try {
      const res = await axios.post(
        baseUrl + DELETE_BILL,
        { BillNo: returnHeader.retNo },
        { withCredentials: true },
      );
      if (res.data.success) {
        setOpen(false); // Close modal
        dispatch(clearTableData());
        dispatch(setToDefaultCalc());
        showSnackbar("success", "Sales return deleted successfully.");
        handleRetNO(); // Get new return number
        dispatch(
          updateSelected({
            edit: false, // Exit edit mode
          }),
        );
      } else {
        showSnackbar(
          "error",
          res.data.message || "Something went wrong, please try again.",
        );
      }
    } catch (error) {
      console.error("Error deleting sales return:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        redirectUrl();
      } else {
        showSnackbar(
          "error",
          "Failed to delete sales return. Please try again.",
        );
      }
    } finally {
      setProgress(false);
    }
  }, [dispatch, returnHeader.retNo, showSnackbar, handleRetNO]);

  // Memoized function to clear all form data
  const handleClearData = useCallback(() => {
    handleRetNO(); // Get a new return number
    dispatch(
      updateSelected({
        edit: false,
        date: dayjs(), // Reset date to current day
        // Clear other header fields if needed, e.g., customer: null, dealer: null, etc.
      }),
    );
    dispatch(clearTableData());
    dispatch(setToDefaultCalc());
    showSnackbar("info", "Form cleared and ready for a new sales return.");
  }, [dispatch, handleRetNO, showSnackbar]);

  // Memoized function for "Save and Print" button
  const saveAndPrint = useCallback(() => {
    handleSubmit("saveAndPrint");
  }, [handleSubmit]);
  const { canDelete, canCreate, canPrint, canAttach, canUpdate } =
    CommonPrivFilter("inventory-sales-return");
  return (
    <>
      <FileUpload
        open={openFileUpload}
        handleClose={handleFileClose}
        setOpen={setOpenFileUpload}
        data={returnHeader.retNo}
        type={mode}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={progress}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Confirmation Modals */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        {modalTyp === "save" ? (
          <Box sx={style}>
            <Typography variant="h3" gutterBottom>
              Save Sales Return
            </Typography>
            <Typography variant="body2" gutterBottom>
              Do you want to save this sales return?
            </Typography>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              gap={2}
              mt={2}
            >
              <Button onClick={handleClose}>No</Button>
              <Stack direction={"row"} gap={2}>
                <Button
                  variant="contained"
                  onClick={() => handleSubmit("withoutPrint")}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={saveAndPrint}
                >
                  Save and Print
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box sx={style}>
            <Typography variant="h3" gutterBottom>
              Delete Sales Return
            </Typography>
            <Typography variant="body2" gutterBottom>
              Are you sure you want to delete this sales return?
            </Typography>
            <Stack direction={"row"} justifyContent={"flex-end"} gap={2} mt={2}>
              <Button onClick={handleClose}>No</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteRet}
              >
                Yes, Delete
              </Button>
            </Stack>
          </Box>
        )}
      </Modal>

      {/* Footer Content */}
      <Box>
        <Grid item xs={12}>
          <Calculation />
        </Grid>
        <Grid container spacing={1} justifyContent={"center"} mt={1}>
          {canCreate ? (
            <Grid item xs={2} md={1}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  ":hover": { background: `${theme.palette.success.dark}` },
                  background: `${theme.palette.grey[500]}`,
                  fontSize: 16,
                }}
                disabled={
                  !getConditionStatus(
                    commonState.userDetail,
                    "Sales Return",
                    "create",
                  ) &&
                  !(
                    returnHeader.edit &&
                    getConditionStatus(
                      commonState.userDetail,
                      "Sales Return",
                      "update",
                    )
                  )
                }
                onClick={() => openModal("save")}
              >
                Save
              </Button>
            </Grid>
          ) : null}

          {canPrint ? (
            <Grid item xs={2} md={1}>
              <Button
                onClick={handlePrint}
                disabled={
                  !returnHeader.edit || // Disable if not in edit mode
                  !getConditionStatus(
                    commonState.userDetail,
                    "Sales Return",
                    "print",
                  )
                }
                fullWidth
                variant="contained"
                color="secondary"
                sx={{
                  background: `${theme.palette.grey[500]}`,
                  fontSize: 16,
                }}
              >
                Print
              </Button>
            </Grid>
          ) : null}
          {canDelete ? (
            <Grid item xs={2} md={1}>
              <Button
                fullWidth
                disabled={
                  !returnHeader.edit || // Disable if not in edit mode
                  !getConditionStatus(
                    commonState.userDetail,
                    "Sales Return",
                    "delete",
                  )
                }
                variant="contained"
                sx={{
                  ":hover": { background: `${theme.palette.error.dark}` },
                  background: `${theme.palette.grey[500]}`,
                  fontSize: 16,
                }}
                onClick={() => openModal("delete")}
              >
                Delete
              </Button>
            </Grid>
          ) : null}
          <Grid item xs={2} md={1}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: `${theme.palette.grey[500]}`,
                fontSize: 16,
              }}
              onClick={handleClearData}
            >
              Clear
            </Button>
          </Grid>
          <Grid item xs={3} md={1}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: `${theme.palette.grey[500]}`,
                overflow: "hidden",
                fontSize: 16,
              }}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Grid>
          {canAttach ? (
            <Grid item xs={2} md={1}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  ":hover": { background: `${"#9e1b7c"}` },
                  background: `${theme.palette.grey[500]}`,
                  fontSize: 16,
                }}
                onClick={handleAttach}
              >
                Attach
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Box>
    </>
  );
}

export default Footer;
